import gql from 'graphql-tag'
import {useQuery, removeTypenameFromObj, useMutation} from 'plugins/apollo'
import {makeQueryHook} from './helpers'

export const clientFields = `
	id
	firstEmail
	firstPhone
	address {
		suite
		unit
		street
		pcode
		city
		region
		countryId
	}
	comms {
		type
		data
		note
	}
	bank {
		IBAN
		BIC
		SWIFT
		name
		number
		address {
			suite
			unit
			street
			pcode
			city
			region
			countryId
		}
	}
	memos {
		ts
		memo
		email {
			subject
			body
		}
	}
	name {
		full
		first
		last
	}
	note
	lang
	creditTs
	knowFrom
	isTravelAgency
	latestCapId
`

// Queries
export const clientsQuery = gql`
	query clientsQuery(
		$limit: Int
		$cursor: ShortString
		$sort: JSON
		$filter: ShortString
		$fullName: ShortString
		$creditTsSet: Boolean
		$saldoMin: Float
		$includeProspects: Boolean
	) {
		clients(
			limit: $limit
			cursor: $cursor
			sort: $sort
			filter: $filter
			fullName: $fullName
			creditTsSet: $creditTsSet
			saldoMin: $saldoMin
			includeProspects: $includeProspects
		) {
			items {
				id
				name {
					full
				}
				latestCapId
				firstPhone
				firstEmail
				address {
					countryId
				}
				lang
			}

			cursor
			total
		}
	}
`

export const basicClientsQuery = gql`
	query clientsQuery(
		$cursor: ShortString
		$filter: ShortString
		$includeProspects: Boolean
		$limit: Int
		$sort: JSON
	) {
		clients(
			limit: $limit
			cursor: $cursor
			filter: $filter
			sort: $sort
			includeProspects: $includeProspects
		) {
			items {
				id
				address {
					street
					city
				}
				firstEmail
				name {
					full
				}
			}
			cursor
			total
		}
	}
`

export const useBasicClientsQuery = makeQueryHook<
	{clients: {items: Pick<Client, 'id' | 'address' | 'firstEmail' | 'name'>[]}},
	{
		cursor?: string
		filter?: string
		includeProspects?: Boolean
		limit?: number
		sort?: JSON
	}
>(basicClientsQuery)

export const clientQuery = gql`
  query clientQuery(
  	$id: ID
  	$email: Email
  ) {
  	client(id: $id, email: $email) {
  		${clientFields}
  	}
  }
`

// TODO: replace it with the new query
export const useClientQueryOld = clientId => {
	const {loading, data, error} = useQuery(clientQuery, {
		variables: {id: clientId},
		skip: !clientId,
	})
	const client =
		loading || data?.client?.id !== clientId ? undefined : data.client

	return {client, loading, error}
}

export const useClientQuery = makeQueryHook<
	{client: Client},
	Pick<Client, 'id' | 'email'>
>(clientQuery)

// Mutations

export const registerClientMutation = [
	gql`
		mutation registerClient($client: ClientInput!) {
			registerClient(client: $client) {
				${clientFields}
				name {
					first
					last
				}
			}
		}
	`,
	{
		name: 'registerClient',
		props: ({registerClient}) => ({
			registerClient: client =>
				registerClient({
					variables: {
						client: removeTypenameFromObj(client, [
							'firstEmail',
							'firstPhone',
							'creditTs',
							'paid',
							'saldo',
							'latestCapId',
						]),
					},
					refetchQueries: ['clientsQuery'],
				}),
		}),
	},
] as const

const REGISTER_CLIENT_MUTATION = gql`
	mutation registerClient($client: ClientInput!) {
		registerClient(client: $client) {
			${clientFields}
			name {
				first
				last
			}
		}
	}
`
export const useRegisterClient = () => {
	const [mutate] = useMutation(REGISTER_CLIENT_MUTATION, {
		refetchQueries: ['clientsQuery'],
	})
	return (client: Partial<Client>) => {
		return mutate({
			variables: {
				client: removeTypenameFromObj(client, [
					'firstEmail',
					'firstPhone',
					'creditTs',
					'paid',
					'saldo',
					'latestCapId',
					'tel',
					'email',
					'conditionsAccept',
					'newsletterAccept',
				]),
			},
		})
	}
}

const SAVE_CLIENT_MUTATION = gql`
  mutation saveClient($client: ClientInput!) {
    saveClient(client: $client) {
      ${clientFields}
      name {
        first
        last
      }
    }
  }
`

export const useSaveClientMutation = () => {
	const [mutate] = useMutation(SAVE_CLIENT_MUTATION, {
		refetchQueries: ['clientsQuery'],
	})

	const saveClient = (client: any) => {
		return mutate({
			variables: {
				client: removeTypenameFromObj(client, [
					'firstEmail',
					'firstPhone',
					'creditTs',
					'paid',
					'saldo',
					'latestCapId',
				]),
			},
		})
	}

	return {
		saveClient,
	}
}

export const removeClientMutation = [
	gql`
		mutation removeClient($id: ID!) {
			removeClient(id: $id)
		}
	`,
	{
		props: ({mutate}) => ({
			removeClient: id =>
				mutate({
					variables: {id},
					refetchQueries: ['clientsQuery'],
				}),
		}),
	},
] as const
